"use client";

import { AreaChart } from "@/shared/ui/AreaChart";
import type { ChartConfig } from "@/shared/ui/ChartContainer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

import { useMemo, useState } from "react";
import type { SalesSummaryParams } from "../../api/client";
import { useSalesByBranchQuery, useSalesByDayQuery } from "../../api/queries";

type SalesReportsContentProps = {
  filters?: SalesSummaryParams;
  groupBy?: "day" | "month";
  onGroupByChange?: (groupBy: "day" | "month") => void;
};

export function SalesReportsContent({
  filters,
  groupBy = "day",
  onGroupByChange,
}: SalesReportsContentProps) {
  const [internalGroupBy, setInternalGroupBy] = useState<"day" | "month">(
    "day",
  );
  const currentGroupBy = groupBy ?? internalGroupBy;
  const handleGroupByChange = onGroupByChange ?? setInternalGroupBy;

  const { data: salesByDayData, isLoading: isLoadingByDay } =
    useSalesByDayQuery({
      ...filters,
      groupBy: currentGroupBy,
    });

  const { data: salesByBranchData, isLoading: isLoadingByBranch } =
    useSalesByBranchQuery(filters);

  const salesChartConfig: ChartConfig = {
    revenue: {
      label: "ລາຍໄດ້",
      color: "hsl(var(--chart-1))",
    },
    subtotal: {
      label: "ລາຍໄດ້ກ່ອນຫັກ",
      color: "hsl(var(--chart-2))",
    },
    discount: {
      label: "ສ່ວນຫຼຸດ",
      color: "hsl(var(--chart-3))",
    },
    paidTotal: {
      label: "ຈ່າຍແລ້ວ",
      color: "hsl(var(--chart-4))",
    },
  };

  // Format data for charts
  const formattedSalesByDay =
    salesByDayData?.map((item) => ({
      date: item.date,
      revenue: Number(item.revenue) || 0,
      subtotal: Number(item.subtotal) || 0,
      discount: Number(item.discount) || 0,
      paidTotal: Number(item.paidTotal) || 0,
    })) ?? [];

  const formattedSalesByBranch =
    salesByBranchData?.map((item) => ({
      branch: item.branchName,
      revenue: Number(item.revenue) || 0,
      orderCount: Number(item.orderCount) || 0,
      avgOrderValue: Number(item.avgOrderValue) || 0,
    })) ?? [];

  // Calculate max revenue for progress bar percentage
  const maxRevenue = useMemo(() => {
    if (formattedSalesByBranch.length === 0) return 1;
    return Math.max(...formattedSalesByBranch.map((item) => item.revenue), 1);
  }, [formattedSalesByBranch]);

  // Format number with thousand separators
  const formatNumber = (num: number) => {
    return num.toLocaleString("lo-LA");
  };

  const isLoading = isLoadingByDay || isLoadingByBranch;

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Card className="rounded-xl border bg-card">
          <CardContent className="p-6">
            <div className="h-64 animate-pulse rounded bg-muted" />
          </CardContent>
        </Card>
        <Card className="rounded-xl border bg-card">
          <CardContent className="p-6">
            <div className="h-64 animate-pulse rounded bg-muted" />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
      <Card className="rounded-xl border bg-card md:col-span-2">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>
            ການຂາຍຕາມ{currentGroupBy === "day" ? "ວັນ" : "ເດືອນ"}
          </CardTitle>
          <div className="flex gap-2">
            <Button
              variant={currentGroupBy === "day" ? "default" : "outline"}
              size="sm"
              onClick={() => handleGroupByChange("day")}
            >
              ຕາມວັນ
            </Button>
            <Button
              variant={currentGroupBy === "month" ? "default" : "outline"}
              size="sm"
              onClick={() => handleGroupByChange("month")}
            >
              ຕາມເດືອນ
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {formattedSalesByDay.length === 0 ? (
            <div className="flex h-64 items-center justify-center text-muted-foreground">
              ບໍ່ມີຂໍ້ມູນສຳລັບການກອງທີ່ເລືອກ
            </div>
          ) : (
            <div className="h-64 w-full min-w-0" style={{ height: "256px" }}>
              <AreaChart
                data={formattedSalesByDay}
                config={salesChartConfig}
                xDataKey="date"
                valueDataKey={["revenue", "subtotal", "discount", "paidTotal"]}
                xAxisTickFormatter={(value) => {
                  if (currentGroupBy === "month") {
                    const [year, month] = String(value).split("-");
                    if (!year || !month) return String(value);
                    return new Date(
                      Number.parseInt(year),
                      Number.parseInt(month) - 1,
                    ).toLocaleDateString("lo-LA", {
                      month: "short",
                      year: "numeric",
                    });
                  }
                  try {
                    const date = new Date(String(value));
                    if (Number.isNaN(date.getTime())) return String(value);
                    return date.toLocaleDateString("lo-LA", {
                      month: "short",
                      day: "numeric",
                    });
                  } catch {
                    return String(value);
                  }
                }}
                stacked={false}
                className="h-full w-full"
                margin={{ left: 12, right: 12, top: 12, bottom: 12 }}
              />
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="rounded-xl border bg-card md:col-span-1">
        <CardHeader>
          <CardTitle>ການຂາຍຕາມສາຂາ</CardTitle>
        </CardHeader>
        <CardContent>
          {formattedSalesByBranch.length === 0 ? (
            <div className="flex h-64 items-center justify-center text-muted-foreground">
              ບໍ່ມີຂໍ້ມູນສຳລັບການກອງທີ່ເລືອກ
            </div>
          ) : (
            <div className="space-y-4">
              {formattedSalesByBranch.map((item) => {
                const progressPercent =
                  maxRevenue > 0 ? (item.revenue / maxRevenue) * 100 : 0;
                return (
                  <div key={item.branch} className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium">{item.branch}</span>
                      <div className="flex items-center gap-4 text-muted-foreground">
                        <span>{formatNumber(item.orderCount)} ອໍເດີ</span>
                        <span className="font-semibold text-foreground">
                          {formatNumber(item.revenue)} LAK
                        </span>
                      </div>
                    </div>
                    <div className="space-y-1">
                      <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                        <div
                          className="h-full bg-primary transition-[width] duration-500"
                          style={{ width: `${progressPercent}%` }}
                        />
                      </div>
                      <div className="flex items-center justify-between text-muted-foreground text-xs">
                        <span>
                          ຄ່າເຊື່ອຍະສະເລ່ຍ: {formatNumber(item.avgOrderValue)} LAK
                        </span>
                        <span>{progressPercent.toFixed(1)}%</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
